import { useState } from 'react';
import { Button, Input, Chip } from '@heroui/react';
import { Plus, GripVertical, Pencil, Check, X } from 'lucide-react';
import { useItemCategories, useCreateItemCategory, useUpdateItemCategory } from '@/api/item-categories';

export default function ItemCategoriesTable() {
  const { data: categories = [], isLoading } = useItemCategories();
  const createCategory = useCreateItemCategory();
  const updateCategory = useUpdateItemCategory();

  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', color: '#e8a020' });

  const handleCreate = () => {
    if (!form.name.trim()) return;
    createCategory.mutate(
      { name: form.name.trim(), color: form.color },
      {
        onSuccess: () => {
          setIsAdding(false);
          setForm({ name: '', color: '#e8a020' });
        },
      },
    );
  };

  const handleUpdate = (id: string) => {
    if (!form.name.trim()) return;
    updateCategory.mutate(
      { id, name: form.name.trim(), color: form.color },
      { onSuccess: () => setEditingId(null) },
    );
  };

  const startEdit = (cat: { id: string; name: string; color: string }) => {
    setEditingId(cat.id);
    setForm({ name: cat.name, color: cat.color });
  };

  return (
    <div
      className="rounded-lg border"
      style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)' }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
        <div>
          <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Item Categories
          </h2>
          <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Categories used in the markup toolbox for typed measurements.
          </p>
        </div>
        <Button
          size="sm"
          color="primary"
          startContent={<Plus size={14} />}
          onPress={() => { setIsAdding(true); setForm({ name: '', color: '#e8a020' }); }}
          className="h-8 text-xs font-medium"
        >
          Add Category
        </Button>
      </div>

      <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
        {/* Header row */}
        <div
          className="grid px-4 py-2 text-[10px] uppercase tracking-wider"
          style={{ gridTemplateColumns: '40px 40px 1fr 80px 60px', color: 'var(--text-muted)' }}
        >
          <span></span><span>Color</span><span>Name</span><span>Status</span><span></span>
        </div>

        {categories.map((cat) => (
          <div
            key={cat.id}
            className="grid items-center px-4 py-2.5"
            style={{ gridTemplateColumns: '40px 40px 1fr 80px 60px', borderColor: 'var(--border)' }}
          >
            <span style={{ color: 'var(--text-muted)', cursor: 'grab' }}>
              <GripVertical size={14} />
            </span>

            {editingId === cat.id ? (
              <>
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  className="w-6 h-6 rounded-full border-0 cursor-pointer"
                />
                <Input
                  size="sm"
                  value={form.name}
                  onValueChange={(v) => setForm((f) => ({ ...f, name: v }))}
                  classNames={{ input: 'text-xs', inputWrapper: 'h-7 min-h-7' }}
                  autoFocus
                />
                <span />
                <div className="flex gap-1">
                  <button onClick={() => handleUpdate(cat.id)} style={{ color: 'var(--green)' }}>
                    <Check size={14} />
                  </button>
                  <button onClick={() => setEditingId(null)} style={{ color: 'var(--text-muted)' }}>
                    <X size={14} />
                  </button>
                </div>
              </>
            ) : (
              <>
                <span
                  className="w-5 h-5 rounded-full"
                  style={{ backgroundColor: cat.color }}
                />
                <span className="text-xs" style={{ color: 'var(--text-primary)' }}>
                  {cat.name}
                </span>
                <Chip
                  size="sm"
                  variant="flat"
                  className="text-[10px] h-5"
                  style={{
                    backgroundColor: cat.is_active
                      ? 'color-mix(in srgb, var(--green) 12%, transparent)'
                      : 'color-mix(in srgb, var(--text-muted) 12%, transparent)',
                    color: cat.is_active ? 'var(--green)' : 'var(--text-muted)',
                  }}
                >
                  {cat.is_active ? 'Active' : 'Inactive'}
                </Chip>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(cat)} style={{ color: 'var(--text-muted)' }}>
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => updateCategory.mutate({ id: cat.id, is_active: !cat.is_active })}
                    className="text-[10px]"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {cat.is_active ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </>
            )}
          </div>
        ))}

        {/* Add row */}
        {isAdding && (
          <div
            className="grid items-center px-4 py-2.5"
            style={{ gridTemplateColumns: '40px 40px 1fr 80px 60px', borderColor: 'var(--border)' }}
          >
            <span />
            <input
              type="color"
              value={form.color}
              onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
              className="w-6 h-6 rounded-full border-0 cursor-pointer"
            />
            <Input
              size="sm"
              value={form.name}
              onValueChange={(v) => setForm((f) => ({ ...f, name: v }))}
              placeholder="Category name"
              classNames={{ input: 'text-xs', inputWrapper: 'h-7 min-h-7' }}
              autoFocus
            />
            <span />
            <div className="flex gap-1">
              <button onClick={handleCreate} style={{ color: 'var(--green)' }}>
                <Check size={14} />
              </button>
              <button onClick={() => setIsAdding(false)} style={{ color: 'var(--text-muted)' }}>
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        {!isLoading && categories.length === 0 && !isAdding && (
          <div className="px-4 py-8 text-center">
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No categories yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}
